import { useCallback, useEffect, useState } from 'react';
import { useLocation } from '@tanstack/react-router';
import { QuickFillHooks } from '@frontend/api-quick-fill';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { PanelPortal, PanelHeader } from '@frontend/panel-engine';
import {
  QuickFillTrayContent,
  resetWaitlistStore,
  WaitListTrayContent,
  WaitlistTrayContext,
  WaitlistTrayContextType,
} from '@frontend/quick-fill';
import { ContentLoader, Heading } from '@frontend/design-system';
import { PaddedContent } from '../../layout/padded-content';
import { useTopBarShallowStore } from '../../store/use-top-bar-store';
import { labelMap } from '../../utils/label-map';
import { TopBarPanelContent } from '../panels/top-bar-panel-scaffold';

const QuickFillPanel = (props: WaitlistTrayContextType) => {
  const { isLoading: isLoadingFF, isWaitlistFFEnabled } = QuickFillHooks.useGetWaitlistFeatureFlagDetails();

  return (
    <PanelPortal enableAnimations id='quickFill' width='medium' onClose={props.onClose}>
      <PaddedContent css={{ paddingBottom: 0 }}>
        <header css={{ display: 'flex', justifyContent: 'space-between' }}>
          <Heading
            level={2}
            css={{ marginBottom: 0, display: 'flex', gap: 8, alignItems: 'center' }}
            data-testid='tray-header'
          >
            {labelMap['quickfill']}
          </Heading>
          <PanelHeader.Close iconName='x' size='large' />
        </header>
      </PaddedContent>
      <TopBarPanelContent>
        {isLoadingFF ? (
          <ContentLoader show />
        ) : isWaitlistFFEnabled ? (
          <WaitlistTrayContext.Provider value={props}>
            <WaitListTrayContent />
          </WaitlistTrayContext.Provider>
        ) : (
          <QuickFillTrayContent closeModal={props.onClose} />
        )}
      </TopBarPanelContent>
    </PanelPortal>
  );
};

export const QuickFillPanelWrapper = () => {
  const { isQuickFillOpen, setQuickFillOpen } = useTopBarShallowStore('isQuickFillOpen', 'setQuickFillOpen');
  const { isWaitlistFFEnabled } = QuickFillHooks.useGetWaitlistFeatureFlagDetails();
  const closeQuickFill = useCallback(() => setQuickFillOpen(false), [setQuickFillOpen]);
  const pathName = useLocation().pathname;
  const [waitlistContextInfo, setWaitlistContextInfo] = useState<Omit<WaitlistTrayContextType, 'onClose'>>({});

  SchedulerV3.useWaitlistCustomEventListener('schedule:waitlist:set-context', (data) => {
    resetWaitlistStore();
    setWaitlistContextInfo({
      selectedLocationId: data.locationId,
      selectedProviderId: data.providerId,
      selectedDate: data.selectedDate,
      selectedTime: data.selectedTime,
    });
  });

  useEffect(() => {
    if (!isQuickFillOpen) {
      // reset context and store when panel is closed
      setWaitlistContextInfo({});
      resetWaitlistStore();
    }
  }, [isQuickFillOpen]);

  useEffect(() => {
    if (isQuickFillOpen && isWaitlistFFEnabled) {
      SchedulerV3.trackPendoEvent('waitlist_viewed', { entryPoint: pathName });
    }
  }, [isQuickFillOpen, isWaitlistFFEnabled, pathName]);

  if (!isQuickFillOpen) {
    return null;
  }
  return <QuickFillPanel onClose={closeQuickFill} {...waitlistContextInfo} />;
};
